import { useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, InputAdornment, Stack, Typography } from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppDialog from "./AppDialog";
import AuthField from "./AuthField";
import { ChangePasswordParams, changePassword } from "../../api";
import { useAuth } from "../../AuthProvider";
import { useCustomColors } from "../../hooks/useCustomColors";
import { useSecureInput } from "../../hooks/useSecureInput";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { secureString } from "../../utils/validation";

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY: ChangePasswordParams = { currentPassword: "", newPassword: "", confirmPassword: "" };

const ChangePasswordDialog = ({ open, onClose }: ChangePasswordDialogProps) => {
  const { t } = useTranslation();
  const colors = useCustomColors();
  const navigate = useNavigate();
  const { endSession } = useAuth();
  const [showPasswords, setShowPasswords] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>();
  // State updates land a render late; the ref closes the double-submit gap.
  const submitting = useRef(false);

  // Checked here first, as the API asks: a typo should not spend a rate-limited attempt.
  const schema = Yup.object({
    currentPassword: secureString(t, { kind: "password", maxLength: 128 }).required(t("validation_password_required")),
    newPassword: secureString(t, { kind: "password", maxLength: 128 })
      .required(t("validation_password_required"))
      .min(8, t("validation_password_min"))
      .test("different", t("change_password_same"), (value, { parent }) => !value || value !== parent.currentPassword),
    confirmPassword: secureString(t, { kind: "password", maxLength: 128 })
      .required(t("validation_confirm_password_required"))
      .test("matches", t("validation_password_not_match"), (value, { parent }) => !value || value === parent.newPassword),
  });

  const { control, handleSubmit, reset, setError } = useForm<ChangePasswordParams>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  });
  const current = useSecureInput({ name: "currentPassword", control, kind: "password", maxLength: 128 });
  const next = useSecureInput({ name: "newPassword", control, kind: "password", maxLength: 128 });
  const confirm = useSecureInput({ name: "confirmPassword", control, kind: "password", maxLength: 128 });

  const close = () => {
    if (isLoading) return;
    reset(EMPTY);
    setApiError(undefined);
    setShowPasswords(false);
    onClose();
  };

  const onSubmit = async (values: ChangePasswordParams) => {
    if (submitting.current) return;
    submitting.current = true;
    setIsLoading(true);
    setApiError(undefined);

    try {
      const { sessionsEnded } = await changePassword(values);
      // The server has already ended this session: drop the token and sign in again.
      toast.success(
        sessionsEnded > 1 ? `${t("change_password_done")} ${t("change_password_other_devices")}` : t("change_password_done")
      );
      reset(EMPTY);
      onClose();
      endSession();
      navigate("/login", { replace: true });
    } catch (err) {
      if (getApiErrorCode(err) === "INVALID_CREDENTIALS") {
        setError("currentPassword", { message: t("change_password_wrong_current") });
      } else {
        setApiError(getApiErrorMessage(err, t("change_password_failed")));
      }
    } finally {
      submitting.current = false;
      setIsLoading(false);
    }
  };

  const visibility = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPasswords(!showPasswords)}
        edge="end"
        aria-label={t(showPasswords ? "game_login_hide_password" : "game_login_show_password")}
        sx={{ color: colors.text.secondary }}
      >
        {showPasswords ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );
  const type = showPasswords ? "text" : "password";

  return (
    <AppDialog open={open} onClose={close} title={t("change_password")} subtitle={t("change_password_subtitle")}>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <AuthField
          label={t("change_password_current")}
          icon={LockRoundedIcon}
          type={type}
          autoComplete="current-password"
          placeholder={t("login_password_placeholder")}
          {...current}
          InputProps={{ endAdornment: visibility }}
        />
        <AuthField
          label={t("change_password_new")}
          icon={LockRoundedIcon}
          type={type}
          autoComplete="new-password"
          placeholder={t("login_password_placeholder")}
          {...next}
        />
        <AuthField
          label={t("change_password_confirm")}
          icon={LockRoundedIcon}
          type={type}
          autoComplete="new-password"
          placeholder={t("login_password_placeholder")}
          {...confirm}
        />

        <Typography sx={{ mb: "12px", fontSize: "12px", lineHeight: 1.6, color: colors.text.secondary }}>
          {t("change_password_signout_note")}
        </Typography>
        {apiError && (
          <Typography role="alert" sx={{ mb: "12px", fontSize: "12.5px", color: colors.accent.danger }}>
            {apiError}
          </Typography>
        )}

        <Button type="submit" fullWidth disabled={isLoading} sx={{ height: "52px", borderRadius: "14px" }}>
          {isLoading ? (
            <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              {t("change_password_saving")}
            </Stack>
          ) : (
            t("change_password_submit")
          )}
        </Button>
      </Box>
    </AppDialog>
  );
};

export default ChangePasswordDialog;
